import { createContext, useContext, useEffect, useRef } from "react";

const CommandPaletteRegistrationContext = createContext(null);

// Pages publish action menus here. The site-level palette reads one snapshot,
// so opening it does not depend on which page is mounted.
const createCommandPaletteRegistrationStore = () => {
  const state = {
    registrations: [],
    nextId: 0,
    snapshot: { options: [], title: "Search" },
  };
  const listeners = new Set();

  const publish = () => {
    const optionLists = state.registrations.map(entry => entry.options);
    const latest = state.registrations[state.registrations.length - 1];
    state.snapshot = {
      options: optionLists.flat(),
      title: latest?.title || "Search",
    };
    listeners.forEach(listener => listener());
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => state.snapshot,
    register(initial = {}) {
      const id = state.nextId + 1;
      state.nextId = id;
      state.registrations = [
        ...state.registrations,
        {
          id,
          options: initial.options || [],
          title: initial.title || "Actions",
        },
      ];
      publish();

      return {
        update(next) {
          const current = state.registrations.find(entry => entry.id === id);
          if (
            !current ||
            (current.options === next.options && current.title === next.title)
          )
            return;
          state.registrations = state.registrations.map(entry =>
            entry.id === id
              ? { ...entry, options: next.options, title: next.title }
              : entry,
          );
          publish();
        },
        unregister() {
          const remaining = state.registrations.filter(
            entry => entry.id !== id,
          );
          if (remaining.length === state.registrations.length) return;
          state.registrations = remaining;
          publish();
        },
      };
    },
  };
};

const useRegisterCommandPaletteCommands = ({ enabled, options, title }) => {
  const store = useContext(CommandPaletteRegistrationContext);
  const registrationReference = useRef(null);
  const optionsReference = useRef(options);
  const titleReference = useRef(title);
  optionsReference.current = options;
  titleReference.current = title;

  useEffect(() => {
    if (!enabled || !store) return undefined;
    const registration = store.register({
      options: optionsReference.current,
      title: titleReference.current,
    });
    registrationReference.current = registration;
    return () => {
      registration.unregister();
      if (registrationReference.current === registration)
        registrationReference.current = null;
    };
  }, [enabled, store]);

  useEffect(() => {
    registrationReference.current?.update({ options, title });
  }, [options, title]);
};

export {
  CommandPaletteRegistrationContext,
  createCommandPaletteRegistrationStore,
  useRegisterCommandPaletteCommands,
};
